const React = require("react");
const { useParams, useSearchParams } = require("react-router-dom");
const interopDefault = (moduleValue) => moduleValue?.default || moduleValue;
const EmblaCarousel = interopDefault(require("../../../src/includes/slider/ProdViewSlider/slider"));
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { useDropshippingResource } = require("../hooks/useDropshippingResource");
const { PageHeader, Button, LinkButton, ResourceState, StatusBadge, KeyValueTable } = require("../components/ui");
const { CJSyncModal } = require("../components/sync-wizard/CJSyncModal");
const {
  normalizeCJProductPreviewData,
  getPreviewForVariant,
  buildVariantFilterGroups,
  selectVariantForFilter,
} = require("../productPreviewData");
const { getProductDropshippingColor } = require("../productDropshippingPreviewVariants");
const h = React.createElement;

// Redesign calqué sur front_elakay/src/includes/pages/productPreview.js :
// galerie image principale + vignettes, sélecteur de variantes (équivalent
// des swatches Couleur/Taille -- change l'image et le prix affichés),
// carte "Order Summary" en colonne de droite (équivalent Buy now/Add Cart),
// onglets Description/Variants/Details sous le bloc principal. Volontairement
// SANS avis clients, produits liés ni comparaison produit : ces sections de
// productPreview dépendent de données Elakay réelles (votes, autres Product,
// ProdCompare) qui n'existent pas pour un article CJ pas encore importé --
// les fabriquer serait malhonnête plutôt qu'un vrai manque de fonctionnalité.
const DropshippingProductDetails = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  // "Back to products" retourne exactement à la page (filtres/pagination)
  // d'où l'utilisateur venait -- voir buildProductDetailsLink dans ui.js,
  // qui pose ce ?from= sur chaque lien vers cette page.
  const backTo = searchParams.get("from") || "/dropshipping/products";
  const { api, t } = useCJDropshipping();
  const resource = useDropshippingResource(() => api.getProduct(productId), [api, productId]);
  const [selectedVariantId, setSelectedVariantId] = React.useState("");
  const [selectedFilters, setSelectedFilters] = React.useState({});
  const [activeTab, setActiveTab] = React.useState("description");
  const [syncModalMode, setSyncModalMode] = React.useState(null); // null | "import" | "sync"
  const product = resource.data?.product;

  React.useEffect(() => {
    setSelectedVariantId("");
    setSelectedFilters({});
    setActiveTab("description");
  }, [productId]);

  const normalizedProduct = React.useMemo(() => normalizeCJProductPreviewData(product), [product]);
  const preview = React.useMemo(
    () => getPreviewForVariant(normalizedProduct, selectedVariantId),
    [normalizedProduct, selectedVariantId]
  );
  const variants = normalizedProduct?.variants || [];
  const variantFilterGroups = React.useMemo(() => buildVariantFilterGroups(variants), [variants]);
  const selectFilter = (key, value) => {
    const result = selectVariantForFilter(variants, selectedFilters, key, value);
    if (!result) return;
    setSelectedFilters(result.selectedFilters);
    setSelectedVariantId(result.variant.id);
  };
  // Le sélecteur de variante fait office d'équivalent Couleur/Taille : choisir
  // une variante remplace l'image principale (si CJ en fournit une propre à
  // cette variante) et le prix/stock affichés, sans toucher à la galerie de
  // base (qui reste navigable indépendamment).
  const currency = preview?.currency || "USD";
  const cost = Number(preview?.costPrice) || 0;
  const suggested = Number(preview?.price) || 0;
  const hasMargin = cost > 0 && suggested > cost;
  const marginPercent = hasMargin ? Math.round(((suggested - cost) / cost) * 100) : 0;
  const displayStock = Number(preview?.stock) || 0;

  return h(React.Fragment, null,
    h(PageHeader, {
      title: product?.title || t("h_cj_product_title", "Product details"),
      action: h(LinkButton, { to: backTo, variant: "secondary" }, t("h_cj_back_products", "Back to products")),
    }),
    h(ResourceState, {
      loading: resource.loading,
      error: resource.error ? t("h_cj_product_load_error", "Impossible de charger ce produit. Vérifiez votre connexion et réessayez.") : "",
      empty: !product,
      onRetry: resource.reload,
      t,
    },
      product ? h(React.Fragment, null,
        h("section", { className: "cjds-pdp" },
          h("div", { className: "cjds-pdp-gallery" },
            h(EmblaCarousel, {
              slides: preview?.images || [],
              options: { loop: false },
              title: preview?.title || product.title,
              previousLabel: t("h_cj_previous_image", "Image précédente"),
              nextLabel: t("h_cj_next_image", "Image suivante"),
            })
          ),

          h("div", { className: "cjds-pdp-info" },
            h(StatusBadge, { status: product.availability, t }),
            h("h2", { className: "cjds-pdp-title" }, preview?.title || product.title),
            product.supplierName || product.brand ? h("p", { className: "cjds-muted" },
              `${t("h_cj_supplier", "Supplier")}: ${product.supplierName || product.brand}`) : null,
            product.description ? h("p", { className: "cjds-pdp-intro" }, product.description) : null,
            h("div", { className: "cjds-pdp-price-row" },
              h("span", { className: "cjds-pdp-price" }, `${(hasMargin ? suggested : cost).toFixed(2)} ${currency}`),
              hasMargin ? h("span", { className: "cjds-original-price" }, `${cost.toFixed(2)} ${currency}`) : null,
              hasMargin ? h("span", { className: "cjds-margin-badge" }, `+${marginPercent}%`) : null
            ),
            h("div", { className: "cjds-row cjds-card-meta" },
              h("span", { className: "cjds-muted" }, `${t("h_cj_stock", "Stock")}: ${displayStock}`),
              h("span", { className: "cjds-muted" }, `SKU: ${preview?.sku || "—"}`),
              (product.deliveryMinDays || product.deliveryMaxDays) ? h("span", { className: "cjds-muted" },
                `${t("h_cj_delivery", "Delivery")}: ${product.deliveryMinDays ?? "?"}-${product.deliveryMaxDays ?? "?"} ${t("h_cj_days", "days")}`
              ) : null
            ),
            product.statusReason ? h("div", { className: "cjds-alert" }, `${t("h_cj_status_reason", "Supplier status")}: ${product.statusReason}`) : null,

            variants.length > 1 ? h("div", { className: "filter-selector-modern cjds-pdp-filter-selector" },
              ...variantFilterGroups.map((group) => h("div", { className: "filter-group", key: group.key },
                h("label", { className: "filter-label" }, group.label),
                h("div", { className: "filter-swatches" },
                  ...group.values.map((value) => {
                    const isColor = group.key === "color" || group.key === "colour";
                    const selected = Object.entries(selectedFilters).some(([key, selectedValue]) =>
                      String(key).trim().toLowerCase().replace(/[\s_-]+/g, " ") === group.key
                      && String(selectedValue).trim().toLowerCase() === String(value).trim().toLowerCase()
                    );
                    const available = Boolean(selectVariantForFilter(variants, selectedFilters, group.label, value));
                    return h("button", {
                      key: String(value),
                      type: "button",
                      disabled: !available,
                      className: `filter-swatch ${isColor ? "circle cjds-color-filter" : "square"} ${available ? "available" : "unavailable"}${selected ? " selected" : ""}`,
                      style: isColor ? { backgroundColor: getProductDropshippingColor(value) } : undefined,
                      onClick: () => selectFilter(group.label, value),
                      "aria-pressed": selected,
                      "aria-label": `${t("h_cj_select_filter", "Sélectionner")} ${group.label}: ${value}`,
                    }, isColor ? h("span", { className: "cjds-visually-hidden" }, value) : h("span", { className: "swatch-label" }, value));
                  })
                )
              ))
            ) : null
          ),

          h("div", { className: "cjds-pdp-summary" },
            h("h4", null, t("h_cj_order_summary", "Order summary")),
            h("div", { className: "cjds-pdp-summary-row" },
              h("span", null, t("h_cj_shipping_stat_sale", "Sales price")),
              h("span", null, `${(hasMargin ? suggested : cost).toFixed(2)} ${currency}`)
            ),
            h("div", { className: "cjds-pdp-summary-row" },
              h("span", null, t("h_cj_shipping_cost_label", "Product cost (CJ)")),
              h("span", null, `${cost.toFixed(2)} ${currency}`)
            ),
            hasMargin ? h("div", { className: "cjds-pdp-summary-row cjds-pdp-summary-row--highlight" },
              h("span", null, t("h_cj_shipping_profit", "Estimated margin")),
              h("span", null, `+${marginPercent}%`)
            ) : null,
            h("div", { className: "cjds-pdp-summary-actions" },
              // Add ET Sync ouvrent le même wizard 3 étapes -- AUCUN appel
              // réseau ici (voir CJSyncModal.finalizeCJProductSynchronization,
              // seul endroit autorisé à créer/modifier le produit Elakay).
              product.importStatus !== "imported"
                ? h(Button, { onClick: () => setSyncModalMode("import") }, t("h_cj_import", "Import to Elakay"))
                : null,
              product.importStatus === "imported"
                ? h(Button, { variant: "secondary", onClick: () => setSyncModalMode("sync") }, t("h_cj_sync", "Sync"))
                : null
            ),
            h("p", { className: "cjds-pdp-summary-note" }, t("h_cj_sourced_from", "Sourced from CJ Dropshipping"))
          )
        ),

        h("div", { className: "cjds-pdp-tabs" },
          h("div", { className: "cjds-pdp-tab-nav" },
            h("button", {
              type: "button",
              className: `cjds-pdp-tab-btn${activeTab === "description" ? " cjds-pdp-tab-btn--active" : ""}`,
              onClick: () => setActiveTab("description"),
            }, t("h_cj_tab_description", "Description")),
            h("button", {
              type: "button",
              className: `cjds-pdp-tab-btn${activeTab === "variants" ? " cjds-pdp-tab-btn--active" : ""}`,
              onClick: () => setActiveTab("variants"),
            }, `${t("h_cj_tab_variants", "Variants")} (${variants.length})`),
            h("button", {
              type: "button",
              className: `cjds-pdp-tab-btn${activeTab === "details" ? " cjds-pdp-tab-btn--active" : ""}`,
              onClick: () => setActiveTab("details"),
            }, t("h_cj_tab_details", "Details"))
          ),

          activeTab === "description" ? (
            product.description
              ? h("div", { className: "cjds-text-description cjds-pdp-description" }, product.description)
              : h("p", { className: "cjds-muted" }, t("h_cj_no_description", "No description available."))
          ) : null,

          activeTab === "variants" ? (
            variants.length ? h("table", { className: "cjds-table" },
              h("thead", null, h("tr", null,
                h("th", null, t("h_cj_variant_name", "Variant")),
                h("th", null, t("h_cj_variant_sku", "SKU")),
                h("th", null, t("h_cj_stock", "Stock")),
                h("th", null, t("h_cj_shipping_col_price", "Price"))
              )),
              h("tbody", null, ...variants.map((variant, index) => h("tr", { key: variant.id || index },
                h("td", null, variant.title || "-"),
                h("td", null, variant.sku || "-"),
                h("td", null, variant.stock || 0),
                h("td", null, `${Number(variant.price || variant.costPrice || 0).toFixed(2)} ${currency}`)
              )))
            ) : h("p", { className: "cjds-muted" }, t("h_cj_no_variants", "No variants for this product."))
          ) : null,

          activeTab === "details" ? h(KeyValueTable, {
            t,
            data: {
              sku: product.sku,
              category: product.category,
              brand: product.brand,
              returnAvailable: product.returnAvailable,
              productUrl: product.productUrl,
              externalProductId: product.externalProductId,
            },
          }) : null
        )
      ) : null
    ),
    h(CJSyncModal, {
      show: Boolean(syncModalMode),
      onHide: () => setSyncModalMode(null),
      mode: syncModalMode || "sync",
      dropshippingProductId: productId,
      elakayProductId: product?.elakayProductId,
      onSynced: resource.reload,
    })
  );
};
module.exports = { DropshippingProductDetails };
